import { useState, useCallback } from "react";
import { useCalibration } from "../../contexts/CalibrationContext";
import Section from "../layout/Section";
import InfoTooltip from "../layout/InfoTooltip";
import SurveyViewer from "./SurveyViewer";
import CalibrationCanvas from "./CalibrationCanvas";
import ShedPositioner from "./ShedPositioner";
import { MODES } from "../../utils/constants";

export default function CalibrationSection() {
  const {
    isSurveyLoaded,
    currentMode,
    isCalibrationComplete,
    isShedPlaced,
    isShedLocked,
    surveyContainerRef,
    startCalibration,
    completeCalibration,
    calculateScaleFactor,
    startShedPlacement,
    lockShed,
    calibPoints,
    resetCalibration,
  } = useCalibration();

  const [showFinalizeBtn, setShowFinalizeBtn] = useState(false);
  const [showSqftModal, setShowSqftModal] = useState(false);
  const [sqftInput, setSqftInput] = useState("");

  const handleCalibrateClick = useCallback(() => {
    if (!isSurveyLoaded) {
      alert("Please upload a survey first.");
      return;
    }
    startCalibration();
    setShowFinalizeBtn(true);
  }, [isSurveyLoaded, startCalibration]);

  const handleFinalizeCalibration = useCallback(() => {
    const { tl, tr, br, bl } = calibPoints;
    if (!tl || !tr || !br || !bl) {
      alert("No vertices have been defined.");
      return;
    }
    setShowSqftModal(true);
  }, [calibPoints]);

  const handleSubmitSqft = useCallback(() => {
    const realArea = parseFloat(sqftInput);

    if (!realArea || realArea <= 0) {
      alert("Invalid input for the real area.");
      return;
    }

    const scaleFactor = calculateScaleFactor(realArea);
    if (scaleFactor) {
      completeCalibration(scaleFactor);
      setShowFinalizeBtn(false);
      setShowSqftModal(false);
      setSqftInput("");
    } else {
      alert("Could not calculate scale factor. Please try again.");
    }
  }, [sqftInput, calculateScaleFactor, completeCalibration]);

  const handleRenewCalibration = useCallback(() => {
    resetCalibration();
    setShowFinalizeBtn(false);
  }, [resetCalibration]);

  const handleShedLocationClick = useCallback(() => {
    if (!isCalibrationComplete) {
      alert("Please complete calibration first.");
      return;
    }
    startShedPlacement();
  }, [isCalibrationComplete, startShedPlacement]);

  const handleLockShed = useCallback(() => {
    lockShed();
  }, [lockShed]);

  const sectionTooltip = (
    <>
      Click on "Calibrate Scale" and mark the 4 corners of the lot on the plan.
      <br />
      Then, enter the square feet (sqft) of the terrain and click "Finish
      Calibration".
      <br />
      This automatically adjusts the plan's scale to correctly locate your
      structure (shed, carport, garage, etc.).
    </>
  );

  return (
    <Section title="Calibration and Shed Location" tooltip={sectionTooltip}>
      <div className="m-4 flex flex-col gap-2 md:flex-row md:gap-4 md:justify-center md:flex-wrap">
        {isCalibrationComplete && (
          <div className="flex justify-center">
            <button
              onClick={handleRenewCalibration}
              className="border-2 border-[#2f5241] text-[#2f5241] hover:bg-[#2f5241] hover:text-white transition-colors duration-200 px-5 py-2 rounded hover:cursor-pointer"
            >
              Start over calibration
            </button>
            <InfoTooltip>
              Reset the current calibration and start over. This will clear all
              calibration points and shed placement data.
            </InfoTooltip>
          </div>
        )}

        <div className="flex justify-center">
          <button
            onClick={handleCalibrateClick}
            disabled={!isSurveyLoaded || isCalibrationComplete}
            className={`bg-[#2f5241] hover:bg-[#2f5241e1] transition-colors duration-200 text-white px-5 py-2 rounded hover:cursor-pointer ${
              !isSurveyLoaded || isCalibrationComplete
                ? "opacity-50 cursor-not-allowed"
                : ""
            }`}
          >
            Calibrate scale
          </button>
          <InfoTooltip>
            Click to show 4 draggable corner points on the survey. Drag each
            corner to match the exact boundaries of your property.
          </InfoTooltip>
        </div>

        {showFinalizeBtn && currentMode === MODES.CALIBRATE && (
          <div className="flex justify-center">
            <button
              onClick={handleFinalizeCalibration}
              className="bg-[#2f5241] hover:bg-[#2f5241e1] text-white px-5 py-2 rounded hover:cursor-pointer"
            >
              Finalize calibration
            </button>
            <InfoTooltip>
              After adjusting the 4 corners, click this button to enter the
              total area in square feet (sqft) of the property.
            </InfoTooltip>
          </div>
        )}

        <div className="flex justify-center">
          <button
            onClick={handleShedLocationClick}
            disabled={!isCalibrationComplete || isShedPlaced}
            className={`bg-[#2f5241] hover:bg-[#2f5241e1] text-white px-5 py-2 rounded hover:cursor-pointer ${
              !isCalibrationComplete || isShedPlaced
                ? "opacity-50 cursor-not-allowed"
                : ""
            }`}
          >
            Shed position
          </button>
          <InfoTooltip>
            After calibration is complete, click this button to place the shed
            on the survey. A square with an X will appear that you can drag and
            rotate to position the shed.
          </InfoTooltip>
        </div>

        {isShedPlaced && !isShedLocked && (
          <div className="flex justify-center">
            <button
              onClick={handleLockShed}
              className="bg-[#2f5241] hover:bg-[#2f5241e1] text-white px-5 py-2 rounded font-semibold hover:cursor-pointer"
            >
              Lock shed position
            </button>
            <InfoTooltip>
              Once the shed is positioned correctly, click this button to lock
              it in place. After locking, the shed position cannot be changed.
            </InfoTooltip>
          </div>
        )}
      </div>

      <div
        ref={surveyContainerRef}
        className="relative w-auto h-auto border border-gray-300 bg-gray-100 cursor-crosshair min-h-50"
      >
        <SurveyViewer />
        <CalibrationCanvas />
        <ShedPositioner />
      </div>

      {showSqftModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl max-w-md mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Enter Property Area
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Enter the total area in square feet (sqft) of the property:
            </p>
            <input
              type="number"
              value={sqftInput}
              onChange={(e) => setSqftInput(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#2f5241] mb-4"
              placeholder="e.g., 5000"
              autoFocus
            />
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowSqftModal(false);
                  setSqftInput("");
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitSqft}
                className="px-4 py-2 text-sm font-medium text-white bg-[#2f5241] rounded-md hover:bg-[#2f5241e1] hover:cursor-pointer"
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      )}
    </Section>
  );
}
